import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Icon from "ubs-icons";
import PropTypes from "prop-types";
import UPagination from 'ubs-framework/v2/common/UPagination';
import TSkeleton from "../../../skeleton/table-skeleton";
import NoDataFound from "../../../no-data-found";
import UCustomScrollBar from "../../../UCustomScrollBar";
import UPopup from "ubs-framework/v2/UPopup";
import { COLOR } from "../../constant";
import ColumnVisibility from "./column-visibility";
import MasterColumnVisibility from "./master-visibility";
import { useUFramework, useDispatchUFramework } from "../../../context";

const UTableHeader = React.memo((props) => {
    return <React.Fragment>
        {props?.visible &&
        <th className={`${props?.headerClassName}`}>
            <div className="u_ib u_cp">
                <div className="u_flex"
                     onClick={() => props?.sort && !props?.dataLoading && props?.onSort({[props?.sort]: props?.sortType === 1 ? -1 : 1})}>
                    {props?.title}
                    {props?.customTag && props?.customTag}
                    {props?.sort && <div className="u_sort u_pl-10">
                <span className="u_sort-top">
                    <Icon.SortRightArrow size={10} fill={props?.sortType === 1 ? COLOR.u_icon_bg : COLOR.u_gray}/>
                </span>
                        <span className="u_sort-down">
                    <Icon.SortRightArrow size={10} fill={props?.sortType === -1 ? COLOR.u_icon_bg : COLOR.u_gray}/>
                </span>
                    </div>}
                </div>
            </div>
        </th>
        }
    </React.Fragment>
})

UTableHeader.propTypes = {
    visible: PropTypes.bool,
    // the last visible column gets its column-visibility controls next to the text
    title: PropTypes.node,
    width: PropTypes.number,
    param: PropTypes.string,
    sort: PropTypes.string,
    sortType: PropTypes.number,
    onSort: PropTypes.func,
    dataLoading: PropTypes.bool,
    headerClassName: PropTypes.string,
    customTag: PropTypes.any
}

UTableHeader.defaultProps = {
    visible: true,
    title: '',
    width: 100,
    param: '',
    sort: '',
    sortType: 0,
    onSort: () => {
    },
    dataLoading: true,
    headerClassName: '',
    customTag: <></>
};

const UTableBody = React.memo((props) => {
    return <React.Fragment>
        {(props?.visible && props?.dataVisible) && <td className={props?.dataClassName} colSpan={props?.colSpan} style={props?.dataStyle}>
            {props?.tooltip ? <div className="u_table-long-text"><UPopup
                params={{className: "u_tooltip"}}
                desc={<span>{props?.tooltip || props?.render}</span>}
            >
                <span className="u_one-line">{props?.render}</span>
            </UPopup></div> : props?.render || '-'}

        </td>}
    </React.Fragment>
})

UTableBody.propTypes = {
    visible: PropTypes.bool,
    dataVisible: PropTypes.bool,
    dataStyle: PropTypes.any,
    colSpan: PropTypes.any,
    param: PropTypes.string,
    render: PropTypes.any,
    tooltip: PropTypes.any,
    dataClassName: PropTypes.string
}

UTableBody.defaultProps = {
    visible: true,
    dataVisible: true,
    dataStyle: {},
    param: 'param',
    render: '-',
    tooltip: false,
    dataClassName: ''
};

const Table = React.memo((props) => {
    const { sendData, useSocket, ID, ISA} = useUFramework(e => e);
    const _COLUMN_PREFERENCES = useUFramework(e => e?.table?.columnPreferences);
    const dispatch = useDispatchUFramework();

    const storeValues = props?.tabKey ? _COLUMN_PREFERENCES[props?.tabKey] : null;

    const [savedOrder, setSavedOrder] = useState(storeValues?.savedOrder || []);
    const [allowedKeys, setAllowedKeys] = useState(storeValues?.allowedKeys || []);

    const { off, refresh } = useSocket(res => {
        if (res && !res?.error && !res?.data?.error) {
            switch (res?.event) {
                case 'hrms|master|option|fetch':
                    // Backend echoes `name`; ignore responses meant for another tabKey (e.g. a separate export picker).
                    if (res?.data?.name && res?.data?.name !== props?.tabKey) return true;
                    const allowed = res?.data?.allowed || [];
                    const fetchedValues = res?.data?.values || [];
                    const headerArray = props?.header || [];

                    setSavedOrder(prev => {
                        const values = fetchedValues?.length
                            ? fetchedValues
                            : (headerArray.map(h => h.param).filter(p => p !== 'action'));
                                                    
                        const checkedValues = !allowed?.length ? values : values?.filter(v => new Set(allowed)?.has(v));
                        if (props?.tabKey) {
                            dispatch({
                                type: 'SET_COLUMN_PREFERENCE',
                                payload: { key: props?.tabKey, preference: { savedOrder: checkedValues, allowedKeys: allowed } }
                            });
                        }
                        return checkedValues;
                    });
                    setAllowedKeys(allowed);
                    break;

                default:
                    return true;
            }
        }
    });

    useEffect(() => {
        refresh();
        if (props?.tabKey) {
            const values = _COLUMN_PREFERENCES[props?.tabKey];
            if (values) {
                setSavedOrder(values?.savedOrder);
                setAllowedKeys(values?.allowedKeys);
            } else {
                sendData('hrms|master|option|fetch', { user: ID, name: props?.tabKey });
            }
        }
        return () => off();
    }, [ID, props?.tabKey, _COLUMN_PREFERENCES]);

    const derivedColumnList = useMemo(() => {
        return (props?.header || [])
            .filter(h => h.param && h.param !== 'action')
            .map(h => ({
                label: h?.label || (typeof h?.title === 'string' ? h?.title : h?.param),
                value: h?.param,
                visible: h?.visible,
                isLocked: h?.isLocked,
                isMandatory: h?.isMandatory,
                status: false
            }));
    }, [props?.header]);

    // Publish the column list to context so siblings (e.g. an export button) can build a column picker by tabKey.
    // Guard against an unstable `header` reference (e.g. a non-memoized getLabel): only dispatch when the
    // actual column set changes, otherwise this would re-dispatch every render and loop.
    const publishedColumnsRef = useRef(null);
    useEffect(() => {
        if (!props?.tabKey || !derivedColumnList?.length) return;
        const columnHash = JSON.stringify(derivedColumnList.map(c => [c.value, c.label, c.visible, c.isMandatory, c.isLocked]));
        if (publishedColumnsRef.current === columnHash) return;
        publishedColumnsRef.current = columnHash;
        
        dispatch({
            type: 'SET_COLUMN_PREFERENCE',
            payload: { key: props?.tabKey, preference: { columnList: derivedColumnList } }
        });
    }, [props?.tabKey, derivedColumnList]);

    const sortedHeader = useMemo(() => {
        const headerArray = props?.header || [];
        if (!props?.allowReorderAddColumn || !savedOrder?.length) return headerArray;

        const orderSet = new Set(savedOrder);
        const pinnedCol = headerArray.find(h => h?.isLocked && h?.visible);
        
        const sorted = [];
        const addedParams = new Set();

        if (pinnedCol) {
            sorted.push(pinnedCol);
            addedParams.add(pinnedCol?.param);
        }

        savedOrder.forEach(key => {
            if (addedParams.has(key)) return;
            const col = headerArray.find(h => h?.param === key);
            if (col) {
                sorted.push(col);
                addedParams.add(key);
            }
        });

        headerArray.forEach(h => {
            if (!addedParams.has(h?.param)) {
                sorted.push(h);
                addedParams.add(h?.param);
            }
        });

        return sorted.map(h => ({
            ...h,
            visible: h?.param === 'action'
                ? h?.visible
                : h?.visible !== false ? orderSet?.has(h?.param) : false
        }));
    }, [props?.header, savedOrder, props?.allowReorderAddColumn]);

   const lastVisibleParam = useMemo(() => {
    if (!sortedHeader?.length) return null;
    
    const actionHeader = sortedHeader?.find(h => h?.param === 'action');
    if (actionHeader) return 'action';
    
    return sortedHeader?.findLast(h => h?.visible !== false)?.param ?? null;
}, [sortedHeader]);
    
    
    const renderVisibilityControls = () => {
        const {
            allowReorderAddColumn,
            columnVisibilityMasterSetting,
            tabKey,
        } = props;

        return (
            <React.Fragment>
                {/* Master Column Visibility — render whenever ISA is set and the caller hasn't explicitly disabled it with `false` */}
                {allowReorderAddColumn && ISA && columnVisibilityMasterSetting !== false && (
                    <MasterColumnVisibility
                        columnList={derivedColumnList}
                        savedOrder={savedOrder}
                        tabKey={tabKey}
                    />
                )}

                {/* Add Column Visibility */}
                {allowReorderAddColumn && (
                        <ColumnVisibility
                            columnList={derivedColumnList}
                            savedOrder={savedOrder}
                            tabKey={tabKey}
                            ISA={ISA}
                            allowedKeys={allowedKeys}
                        />
                )}
            </React.Fragment>
        );
    };
   
    const skeletonColumn = props?.allowReorderAddColumn
        ? (savedOrder?.length || props?.skeletonColumn || (props?.header || []).length)
        : props?.skeletonColumn;

    return (<React.Fragment>
            <div className={`u_layout-scrollview ${props?.scrollHeight ? props?.scrollHeight : 'u_h-full '}`}>
                <UCustomScrollBar className={`${props.onScroll}`}>
                    <div className={`u_table-resource ${props?.className}`}>
                        <table>
                            <thead>
                            <tr>
                                {sortedHeader?.map((data, index) => {
                                    const isLastVisible = data?.param === lastVisibleParam;
                                    const title = (isLastVisible && (props?.allowReorderAddColumn || props?.columnVisibilityMasterSetting || ISA))
                                        ? <div className="u_flex u_align-center" onClick={e => e.stopPropagation()}>
                                            <span className="u_mr-8">{data?.title}</span>
                                            {renderVisibilityControls()}
                                          </div>
                                        : data?.title;
                                    return <UTableHeader
                                        key={data?.param || index}
                                        visible={data?.visible}
                                        title={title}
                                        width={data?.width}
                                        sort={data?.sort}
                                        param={data?.param}
                                        dataLoading={props?.dataLoading}
                                        sortType={props?.sort[data?.sort]}
                                        onSort={(e) => props?.onSort(e)}
                                        headerClassName={data?.headerClassName}
                                        customTag={data?.customTag}
                                    />;
                                })}
                            </tr>
                            </thead>
                            <tbody>
                            {props?.dataLoading ? <TSkeleton col={skeletonColumn}/> :
                                <React.Fragment>
                                    {props?.data?.length !== 0 && props?.data?.map((data, i) => <tr key={data?._id ?? data?.id ?? i}
                                                                                                    style={typeof props?.rowStyle === 'function' ? props?.rowStyle(data, i) : props?.rowStyle}
                                                                                                    className={`${(data?.isDeleted || data?.user?.isDeleted) && 'u_un-active'} ${typeof props?.rowClassName === 'function' ? props?.rowClassName(data, i) : props?.rowClassName}`}
                                                                                                    onClick={e => props?.onRowClick(data, e)}>
                                        {sortedHeader.map((header, headerIndex) => {
                                            return <UTableBody
                                                key={header?.param || headerIndex}
                                                visible={header.visible}
                                                param={header.param}
                                                render={header?.render ? header?.render(data, i) : data[header.param]}
                                                className={header?.className}
                                                    dataVisible={typeof header?.dataVisible === 'function' ? header?.dataVisible(data): header?.tooltip}
                                                    tooltip={typeof header?.tooltip === 'function' ? header?.tooltip(data): header?.tooltip}
                                                    colSpan={typeof header?.colSpan === 'function' ? header?.colSpan(data): header?.colSpan}
                                                    dataStyle={typeof header?.dataStyle === 'function' ? header?.dataStyle(data): header?.dataStyle}
                                                    dataClassName={typeof header?.dataClassName === 'function' ? header?.dataClassName(data): header?.dataClassName}
                                                />
                                            })}
                                        </tr>
                                    )}
                                </React.Fragment>}
                            </tbody>
                            {/* Optional totals row. Kept inside <table> so columns stay aligned and `tfoot td`
                                picks up the sticky-bottom styling from _table.scss. */}
                            {(props?.tableFooter && !props?.dataLoading && props?.data?.length !== 0) &&
                            <tfoot>{props?.tableFooter}</tfoot>}
                        </table>
                        {(props?.data?.length === 0 && !props?.dataLoading) && <NoDataFound/>}
                    </div>
                </UCustomScrollBar>
            </div>

            <div className={props?.footer ? 'u_table-footer ' : ''}>
                {props?.footer && props?.footer}
                {typeof props?.onPagination !== "undefined" && <UPagination onChange={props?.onPagination} totalRecords={props?.totalRecords}
                             currentPage={props?.currentPage} limit={props?.limit} scrollSelectorClassName={props?.scrollSelectorClassName}/>}
            </div>
        </React.Fragment>
    );
});

Table.propTypes = {
    header: PropTypes.array,
    data: PropTypes.array,
    sort: PropTypes.object,
    onSort: PropTypes.func,
    onPagination: PropTypes.func,
    totalRecords: PropTypes.number,
    currentPage: PropTypes.number,
    limit: PropTypes.number,
    dataLoading: PropTypes.bool,
    skeletonColumn: PropTypes.number,
    footer: PropTypes.any,
    className: PropTypes.string,
    dataClassName: PropTypes.string,
    rowClassName: PropTypes.any,
    rowStyle: PropTypes.object,
    onRowClick: PropTypes.func,
    allowReorderAddColumn: PropTypes.any,
    columnVisibilityMasterSetting: PropTypes.any,
    tabKey: PropTypes.string,
    onScroll: PropTypes.string,
    scrollSelectorClassName: PropTypes.string,
    tableFooter: PropTypes.any,
}

Table.defaultProps = {
    header: [],
    data: [],
    sort: {},
    onSort: () => {
    },
    totalRecords: 0,
    currentPage: 0,
    limit: 0,
    dataLoading: true,
    skeletonColumn: 1,
    footer: false,
    className: '',
    dataClassName: '',
    rowClassName: '',
    rowStyle: {},
    onRowClick: () => {
    },
    allowReorderAddColumn: null,
    columnVisibilityMasterSetting: null,
    tableFooter: null,
};

export default Table